/**
 * Stale-while-revalidate read primitive.
 *
 * Behaviour:
 *   - Cold miss (no entry in cache):
 *       await fetcher() → write to cache → return { data, isStale: false }.
 *   - Fresh hit (entry exists, age <= staleTime):
 *       return { data, isStale: false } immediately. No background fetch.
 *   - Stale hit (entry exists, age > staleTime):
 *       return { data, isStale: true } immediately AND fire fetcher() in the
 *       background to refresh. Background fetcher errors are logged + dropped
 *       (Rule 8 does not apply: this is not a user-action handler — it is an
 *       internal best-effort revalidation. The user-action read already
 *       resolved with the stale data.).
 *
 * This is the read-path equivalent of TanStack Query's `staleTime` semantics
 * without the surrounding query-key index, subscriber bookkeeping, or
 * suspense integration. ~70 lines of code; ~600 B gzipped including key.ts +
 * store.ts.
 *
 * In-flight de-duplication:
 *   Multiple concurrent calls for the same key share the same in-flight
 *   promise. Without this, opening a recipe that issues `recipe.get` 3× from
 *   different effects within the same render burst would fire 3 round trips.
 */

import * as store from "./store.js";
import type { CacheEntry } from "./store.js";

/** Hot-path: in-flight fetcher promises de-duped by key. */
const inFlight = new Map<string, Promise<unknown>>();

export interface SwrParams<T> {
  /** Stable cache key (built by `cacheKey({...})`). */
  key: string;
  /**
   * The actual network call. Must be idempotent / safe to call from a
   * background revalidation tick. The cached value is the resolved value of
   * this promise, with no transformation.
   */
  fetcher: () => Promise<T>;
  /**
   * Maximum age (ms) at which a cached entry is still considered fresh. Past
   * this point the entry is returned but a background revalidation fires.
   *
   * Suggested defaults (per the dispatch brief):
   *   recipe.list   → 30_000
   *   recipe.get    → 30_000
   *   attempt.list  → 15_000
   *   collection.list → 60_000
   */
  staleTime: number;
  /**
   * Optional callback fired when a stale-hit background revalidation lands
   * fresh data, AFTER the result has been written to the cache. Lets the
   * caller re-set UI state so a stale first render is corrected.
   *
   * Not fired on fresh hits or cold misses — the data the read returned is
   * already current. Not fired when the revalidation fails (the stale value
   * stands). When a concurrent revalidation for the same key is already
   * in-flight, only the first caller's `onUpdate` fires (in-flight dedup).
   *
   * Callback errors are logged + dropped — a UI-side throw must not poison
   * the cache write that already succeeded.
   */
  onUpdate?: (data: T) => void;
}

export interface SwrResult<T> {
  data: T;
  /** True iff the value was served stale and a background refresh is in-flight. */
  isStale: boolean;
  /**
   * True iff the returned `data` came off the cache (a fresh OR a stale hit);
   * false only on a cold miss where `data` is the resolved result of a live
   * network `fetcher()` call.
   *
   * Distinct from `isStale`: a fresh hit is `{ isStale: false, fromCache: true }`.
   * Consumers that must treat an empty payload as authoritative ONLY when it
   * came off the wire (never when replayed from a possibly-stale cache) read
   * this flag — see app.tsx `applyRecipeListPayload`.
   */
  fromCache: boolean;
}

/**
 * SWR read. Always returns a value — either from cache (possibly stale) or
 * from a network round trip.
 */
export async function getCached<T>(params: SwrParams<T>): Promise<SwrResult<T>> {
  const { key, fetcher, staleTime, onUpdate } = params;
  const now = Date.now();

  const cached = (await store.get<T>(key)) as CacheEntry<T> | null;
  if (cached) {
    const age = now - cached.storedAt;
    if (age <= staleTime) {
      // Fresh hit — return synchronously fast (sub-millisecond from L1).
      return { data: cached.data, isStale: false, fromCache: true };
    }
    // Stale hit — return cached value AND kick off a background refresh.
    void revalidate(key, fetcher, onUpdate);
    return { data: cached.data, isStale: true, fromCache: true };
  }

  // Cold miss — must await the network. De-duplicate concurrent callers.
  return { data: await execAndStore(key, fetcher), isStale: false, fromCache: false };
}

/**
 * Background revalidation. Fires fetcher() once and writes the result back to
 * cache. Errors are logged + dropped — the foreground caller already has a
 * value (the stale one).
 *
 * De-dupe via `inFlight` so two stale-hits in the same tick don't double-fire.
 */
function revalidate<T>(
  key: string,
  fetcher: () => Promise<T>,
  onUpdate?: (data: T) => void,
): Promise<void> {
  if (inFlight.has(key)) return inFlight.get(key) as Promise<void>;
  const p = (async () => {
    try {
      const data = await fetcher();
      await store.set(key, data);
      if (onUpdate) {
        try {
          onUpdate(data);
        } catch (err) {
          // A UI-side callback throw must not poison the cache write above.
          console.warn("[proof.cache] revalidation onUpdate callback failed", { key, err });
        }
      }
    } catch (err) {
      // Background revalidation is best-effort. The user-facing read already
      // resolved with the stale value; logging here helps diagnose silent
      // staleness in dev without breaking the read.
      console.warn("[proof.cache] background revalidation failed", { key, err });
    } finally {
      inFlight.delete(key);
    }
  })();
  inFlight.set(key, p);
  return p;
}

/**
 * Cold-miss path: await the fetcher, write the result, return the data.
 * De-dupes against an already-in-flight call for the same key.
 */
async function execAndStore<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const p = (async () => {
    try {
      const data = await fetcher();
      await store.set(key, data);
      return data;
    } finally {
      inFlight.delete(key);
    }
  })();
  inFlight.set(key, p);
  return p;
}

/**
 * Test-only: clear the in-flight map between tests so a leaked promise from
 * one case cannot affect the next.
 */
export function __resetInFlightForTest(): void {
  inFlight.clear();
}
